import { ReactElement, useRef, useState } from 'react';

import { Form, Select, SelectProps } from 'antd';
import { Rule } from 'antd/es/form';
import { NamePath } from 'antd/es/form/interface';
import classNames from 'classnames';

import { ArrowDown } from '@/components/icons/ArrowDown';

import { getArticle } from '@/utils/textUtils';

import './SelectFormItem.scss';

interface IDefaultOptionType<T> {
	label: string;
	value: T;
	children?: Omit<IDefaultOptionType<T>, 'children'>[];
	disabled?: boolean;
	className?: string;
	title?: string;
	[key: string]: any;
}

interface ISelectFormItemProps<T = string> extends SelectProps<T, IDefaultOptionType<T>> {
	name: NamePath;
	marginBottom?: number;
	className?: string;
	fullName?: NamePath;
	label?: string;
	rules?: Rule[];
	defaultValue?: any;
}

const SelectFormItem = <T,>({
	name,
	className,
	fullName,
	options,
	rules,
	defaultValue,
	marginBottom = 16,
	label = '',
	placeholder = 'Select from the list',
	...rest
}: ISelectFormItemProps<T>): ReactElement => {
	const form = Form.useFormInstance();

	const [open, setOpen] = useState(false);

	const selectedByKeyValue = useRef<T | null>(null);

	const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
		if (rest.showSearch || !options?.length) {
			return;
		}

		if (e.key === 'Enter' && selectedByKeyValue.current) {
			e.stopPropagation();

			form.setFieldValue(fullName ?? name, selectedByKeyValue.current);

			setOpen(false);
			return;
		}

		const selectedOption = options.find((option) => {
			if (!option.label || typeof option.label !== 'string') {
				return false;
			}
			return option.label.toLowerCase().startsWith(e.key.toLowerCase());
		});

		if (!selectedOption) {
			selectedByKeyValue.current = null;
			return;
		}

		form.setFieldValue(fullName ?? name, selectedOption.value);

		const activeElement = document.querySelector(
			`.ant-select-dropdown .ant-select-item[title="${selectedOption.label}"]`,
		) as HTMLElement;

		selectedByKeyValue.current = selectedOption.value as T | null;

		document
			.querySelector('.ant-select-dropdown .ant-select-item-option-active')
			?.classList.remove('ant-select-item-option-active');

		activeElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		activeElement?.classList.add('ant-select-item-option-active');
	};

	return (
		<Form.Item
			className={classNames('select', className)}
			initialValue={defaultValue}
			label={label}
			name={name}
			rules={
				rules ?? [
					{
						required: true,
						message: `Please select ${getArticle(label)} ${label}`,
					},
				]
			}
			style={{ marginBottom }}
		>
			<Select
				classNames={{
					popup: {
						root: 'select-popup',
					},
				}}
				listItemHeight={45.14}
				open={open}
				options={options}
				placeholder={placeholder}
				suffixIcon={<ArrowDown />}
				virtual={false}
				onKeyDown={handleKeyDown}
				onOpenChange={(value) => setOpen(value)}
				{...rest}
			/>
		</Form.Item>
	);
};

export default SelectFormItem;
